import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    AiOutlineCamera,
    AiOutlineDelete,
    AiOutlineEye,
    AiOutlineEyeInvisible,
    AiOutlineLock,
    AiOutlineSave,
    AiOutlineUser,
    AiOutlineCreditCard,
} from 'react-icons/ai';
import AxiosUrl from '../AxiosUrl';
import styles from './Profile.module.css';

const CDN_UPLOAD = 'https://cdn.ecomtechbd.com/upload-normal';
const AVATAR_SIZE = 400; // px, square
const MAX_SOURCE_BYTES = 10 * 1024 * 1024;

const PLAN_LABELS = {
    trial: 'Trial',
    startup: 'Startup',
    smallBusiness: 'Small Business',
    business: 'Business',
    enterprise: 'Enterprise',
    custom: 'Custom',
};

const STATUS_STYLE = {
    active: 'pillGreen',
    trial: 'pillBlue',
    pending_payment: 'pillYellow',
    expired: 'pillRed',
    suspended: 'pillRed',
    cancelled: 'pillGrey',
};

const humanize = (s) =>
    String(s || '')
        .replace(/_/g, ' ')
        .replace(/^\w/, (c) => c.toUpperCase());

const fmtDate = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? '—' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const initials = (name) =>
    String(name || '')
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase() || 'A';

// Center-crop any image to a square and shrink it, so avatars stay small and sharp.
function toSquareJpeg(file, size) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            const side = Math.min(img.naturalWidth, img.naturalHeight);
            const sx = (img.naturalWidth - side) / 2;
            const sy = (img.naturalHeight - side) / 2;
            const out = Math.min(size, side);
            const canvas = document.createElement('canvas');
            canvas.width = out;
            canvas.height = out;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, out, out);
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, sx, sy, side, side, 0, 0, out, out);
            URL.revokeObjectURL(url);
            canvas.toBlob(
                (blob) => (blob ? resolve(blob) : reject(new Error('Could not process the image'))),
                'image/jpeg',
                0.88
            );
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('This file is not a supported image'));
        };
        img.src = url;
    });
}

const errText = (err, fallback) => {
    const d = err?.response?.data;
    if (typeof d === 'string' && d) return d;
    return d?.message || d?.msg || d?.error || fallback;
};

function Profile() {
    const uid = localStorage.getItem('id');
    const navigate = useNavigate();
    const fileRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState(null);
    const [form, setForm] = useState({ name: '', email: '', address: '' });
    const [saving, setSaving] = useState(false);
    const [avatarBusy, setAvatarBusy] = useState('');

    const [pw, setPw] = useState({ oldPass: '', newPass: '', confirm: '' });
    const [showPw, setShowPw] = useState(false);
    const [pwSaving, setPwSaving] = useState(false);

    // Keep the header (Layout) in sync with the profile.
    const broadcast = useCallback((p) => {
        try {
            localStorage.setItem('username', p.name || 'Admin');
            if (p.avatar) localStorage.setItem('avatar', p.avatar);
            else localStorage.removeItem('avatar');
        } catch (e) {
            /* storage blocked */
        }
        window.dispatchEvent(new CustomEvent('ej-profile-updated', { detail: { name: p.name, avatar: p.avatar } }));
    }, []);

    const applyProfile = useCallback(
        (p) => {
            setProfile(p);
            setForm({ name: p.name || '', email: p.email || '', address: p.address || '' });
            broadcast(p);
        },
        [broadcast]
    );

    useEffect(() => {
        let alive = true;
        AxiosUrl.get(`profile/${uid}`)
            .then((res) => {
                if (alive && res.data && typeof res.data === 'object') applyProfile(res.data);
            })
            .catch((err) => toast.error(errText(err, 'Could not load your profile')))
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [uid, applyProfile]);

    const dirty =
        profile &&
        (form.name.trim() !== (profile.name || '') ||
            form.email.trim().toLowerCase() !== (profile.email || '') ||
            form.address.trim() !== (profile.address || ''));

    const onField = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const saveProfile = async (e) => {
        e.preventDefault();
        if (form.name.trim().length < 2) return toast.error('Enter your full name');
        try {
            setSaving(true);
            const res = await AxiosUrl.post(`profile/${uid}`, {
                name: form.name.trim(),
                email: form.email.trim(),
                address: form.address.trim(),
            });
            if (res.data?.profile) applyProfile(res.data.profile);
            toast.success('Profile updated');
        } catch (err) {
            toast.error(errText(err, 'Could not save your profile'));
        } finally {
            setSaving(false);
        }
    };

    const saveAvatar = async (url) => {
        const res = await AxiosUrl.post(`profile/${uid}`, { avatar: url });
        if (res.data?.profile) applyProfile(res.data.profile);
    };

    const onPickAvatar = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        if (!/^image\//.test(file.type)) return toast.error('Choose an image file (JPG, PNG or WebP)');
        if (file.size > MAX_SOURCE_BYTES) return toast.error('Image is too large (max 10 MB)');
        try {
            setAvatarBusy('Uploading…');
            const blob = await toSquareJpeg(file, AVATAR_SIZE);
            const fd = new FormData();
            fd.append('photo', blob, 'avatar.jpg');
            let url;
            try {
                const up = await axios.post(CDN_UPLOAD, fd);
                url = typeof up.data === 'string' ? up.data.trim() : '';
            } catch (err) {
                throw new Error('The picture could not be uploaded. Check your connection and try again.');
            }
            if (!/^https:\/\//.test(url)) throw new Error('The picture could not be uploaded. Please try again.');
            await saveAvatar(url);
            toast.success('Profile picture updated');
        } catch (err) {
            toast.error(err?.response ? errText(err, 'Could not save the picture') : err.message);
        } finally {
            setAvatarBusy('');
        }
    };

    const removeAvatar = async () => {
        if (!window.confirm('Remove your profile picture?')) return;
        try {
            setAvatarBusy('Removing…');
            await saveAvatar('');
            toast.success('Profile picture removed');
        } catch (err) {
            toast.error(errText(err, 'Could not remove the picture'));
        } finally {
            setAvatarBusy('');
        }
    };

    const changePassword = async (e) => {
        e.preventDefault();
        if (!pw.oldPass || !pw.newPass) return toast.error('Enter your current and new password');
        if (pw.newPass.length < 6) return toast.error('New password must be at least 6 characters');
        if (pw.newPass !== pw.confirm) return toast.error('New passwords do not match');
        try {
            setPwSaving(true);
            const res = await AxiosUrl.post(`change-password/${uid}`, { oldPass: pw.oldPass, newPass: pw.newPass });
            if (res.data?.success === false) throw new Error(res.data.message);
            setPw({ oldPass: '', newPass: '', confirm: '' });
            toast.success('Password changed');
        } catch (err) {
            toast.error(err?.response ? errText(err, 'Could not change the password') : err.message);
        } finally {
            setPwSaving(false);
        }
    };

    const strength = (() => {
        const p = pw.newPass;
        if (!p) return null;
        let s = 0;
        if (p.length >= 8) s += 1;
        if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s += 1;
        if (/\d/.test(p)) s += 1;
        if (/[^A-Za-z0-9]/.test(p)) s += 1;
        if (p.length < 6) return { label: 'Too short', cls: 'weak', pct: 15 };
        if (s <= 1) return { label: 'Weak', cls: 'weak', pct: 35 };
        if (s <= 2) return { label: 'Fair', cls: 'fair', pct: 60 };
        return { label: 'Strong', cls: 'strong', pct: 100 };
    })();

    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <div className={styles.heroSkeleton}>
                        <div className={`${styles.skeleton} ${styles.skeletonAvatar}`} />
                        <div style={{ flex: 1 }}>
                            <div className={`${styles.skeleton} ${styles.skeletonLine}`} style={{ width: '40%' }} />
                            <div className={`${styles.skeleton} ${styles.skeletonLine}`} style={{ width: '25%' }} />
                        </div>
                    </div>
                </div>
                <div className={styles.card}>
                    <div className={`${styles.skeleton} ${styles.skeletonLine}`} />
                    <div className={`${styles.skeleton} ${styles.skeletonLine}`} style={{ width: '70%' }} />
                </div>
            </div>
        );
    }

    if (!profile) {
        return (
            <div className={styles.container}>
                <ToastContainer position="top-right" autoClose={3000} />
                <div className={styles.card}>
                    <p className={styles.text}>Your profile could not be loaded. Refresh the page to try again.</p>
                </div>
            </div>
        );
    }

    const acc = profile.account || {};
    const statusCls = styles[STATUS_STYLE[acc.subscriptionStatus] || 'pillGrey'];

    return (
        <div className={styles.container}>
            <ToastContainer position="top-right" autoClose={3000} />

            {/* Hero: avatar + identity */}
            <div className={`${styles.card} ${styles.hero}`}>
                <div className={styles.avatarWrap}>
                    {profile.avatar ? (
                        <img className={styles.avatar} src={profile.avatar} alt={profile.name || 'Profile'} />
                    ) : (
                        <div className={`${styles.avatar} ${styles.avatarInitials}`}>{initials(profile.name)}</div>
                    )}
                    {avatarBusy && <div className={styles.avatarBusy}>{avatarBusy}</div>}
                    <button
                        type="button"
                        className={styles.avatarCamera}
                        onClick={() => fileRef.current && fileRef.current.click()}
                        disabled={!!avatarBusy}
                        title="Change profile picture"
                        aria-label="Change profile picture"
                    >
                        <AiOutlineCamera />
                    </button>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className={styles.hiddenInput}
                        onChange={onPickAvatar}
                    />
                </div>

                <div className={styles.heroInfo}>
                    <h1 className={styles.heroName}>{profile.name || 'Your name'}</h1>
                    <p className={styles.heroMeta}>
                        {profile.mobile}
                        {profile.email ? ` · ${profile.email}` : ''}
                    </p>
                    <div className={styles.heroPills}>
                        {acc.plan && <span className={`${styles.pill} ${styles.pillBlue}`}>{PLAN_LABELS[acc.plan] || humanize(acc.plan)} plan</span>}
                        {acc.subscriptionStatus && <span className={`${styles.pill} ${statusCls}`}>{humanize(acc.subscriptionStatus)}</span>}
                        <span className={styles.muted}>Member since {fmtDate(acc.memberSince)}</span>
                    </div>
                    <div className={styles.heroActions}>
                        <button
                            type="button"
                            className={styles.btnSecondary}
                            onClick={() => fileRef.current && fileRef.current.click()}
                            disabled={!!avatarBusy}
                        >
                            <AiOutlineCamera /> {profile.avatar ? 'Change photo' : 'Upload photo'}
                        </button>
                        {profile.avatar && (
                            <button type="button" className={styles.btnGhostDanger} onClick={removeAvatar} disabled={!!avatarBusy}>
                                <AiOutlineDelete /> Remove
                            </button>
                        )}
                    </div>
                    <p className={styles.hint}>JPG, PNG or WebP. It is cropped to a square automatically.</p>
                </div>
            </div>

            <div className={styles.grid}>
                {/* Profile information */}
                <form className={styles.card} onSubmit={saveProfile}>
                    <div className={styles.cardHeader}>
                        <AiOutlineUser className={styles.cardIcon} />
                        <h2 className={styles.cardTitle}>Personal information</h2>
                    </div>

                    <div className={styles.field}>
                        <label htmlFor="pf-name">Full name</label>
                        <input id="pf-name" name="name" value={form.name} onChange={onField} maxLength={80} placeholder="Your full name" autoComplete="name" />
                    </div>

                    <div className={styles.row}>
                        <div className={styles.field}>
                            <label htmlFor="pf-email">Email</label>
                            <input
                                id="pf-email"
                                name="email"
                                type="email"
                                value={form.email}
                                onChange={onField}
                                maxLength={120}
                                placeholder="you@example.com"
                                autoComplete="email"
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="pf-mobile">Mobile number</label>
                            <input id="pf-mobile" value={profile.mobile} readOnly disabled />
                            <span className={styles.hint}>Used to sign in, so it can't be changed here.</span>
                        </div>
                    </div>

                    <div className={styles.field}>
                        <label htmlFor="pf-address">Address</label>
                        <textarea
                            id="pf-address"
                            name="address"
                            rows={3}
                            value={form.address}
                            onChange={onField}
                            maxLength={300}
                            placeholder="House, road, area, city"
                            autoComplete="street-address"
                        />
                    </div>

                    <div className={styles.actions}>
                        {dirty && (
                            <button
                                type="button"
                                className={styles.btnSecondary}
                                onClick={() => setForm({ name: profile.name || '', email: profile.email || '', address: profile.address || '' })}
                                disabled={saving}
                            >
                                Discard
                            </button>
                        )}
                        <button type="submit" className={styles.btnPrimary} disabled={!dirty || saving}>
                            <AiOutlineSave /> {saving ? 'Saving…' : 'Save changes'}
                        </button>
                    </div>
                </form>

                <div className={styles.side}>
                    {/* Account */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <AiOutlineCreditCard className={styles.cardIcon} />
                            <h2 className={styles.cardTitle}>Account</h2>
                        </div>
                        <dl className={styles.facts}>
                            <div>
                                <dt>Plan</dt>
                                <dd>{acc.plan ? PLAN_LABELS[acc.plan] || humanize(acc.plan) : '—'}</dd>
                            </div>
                            <div>
                                <dt>Status</dt>
                                <dd>{acc.subscriptionStatus ? humanize(acc.subscriptionStatus) : '—'}</dd>
                            </div>
                            <div>
                                <dt>Billing</dt>
                                <dd>{acc.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}</dd>
                            </div>
                            <div>
                                <dt>Next billing</dt>
                                <dd>{fmtDate(acc.nextBilling)}</dd>
                            </div>
                            <div>
                                <dt>SMS balance</dt>
                                <dd>৳{Number(acc.smsBalance || 0).toLocaleString()}</dd>
                            </div>
                        </dl>
                        <button type="button" className={styles.btnLink} onClick={() => navigate('/billing')}>
                            Manage billing →
                        </button>
                    </div>

                    {/* Password */}
                    <form className={styles.card} onSubmit={changePassword}>
                        <div className={styles.cardHeader}>
                            <AiOutlineLock className={styles.cardIcon} />
                            <h2 className={styles.cardTitle}>Change password</h2>
                            <button
                                type="button"
                                className={styles.iconBtn}
                                onClick={() => setShowPw((s) => !s)}
                                title={showPw ? 'Hide passwords' : 'Show passwords'}
                            >
                                {showPw ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                            </button>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="pw-old">Current password</label>
                            <input
                                id="pw-old"
                                type={showPw ? 'text' : 'password'}
                                value={pw.oldPass}
                                onChange={(e) => setPw({ ...pw, oldPass: e.target.value })}
                                autoComplete="current-password"
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="pw-new">New password</label>
                            <input
                                id="pw-new"
                                type={showPw ? 'text' : 'password'}
                                value={pw.newPass}
                                onChange={(e) => setPw({ ...pw, newPass: e.target.value })}
                                autoComplete="new-password"
                            />
                            {strength && (
                                <div className={styles.strength}>
                                    <div className={styles.strengthTrack}>
                                        <div className={`${styles.strengthBar} ${styles[strength.cls]}`} style={{ width: `${strength.pct}%` }} />
                                    </div>
                                    <span className={styles.hint}>{strength.label}</span>
                                </div>
                            )}
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="pw-confirm">Confirm new password</label>
                            <input
                                id="pw-confirm"
                                type={showPw ? 'text' : 'password'}
                                value={pw.confirm}
                                onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                                autoComplete="new-password"
                            />
                            {pw.confirm && pw.confirm !== pw.newPass && <span className={styles.error}>Passwords do not match</span>}
                        </div>
                        <div className={styles.actions}>
                            <button type="submit" className={styles.btnPrimary} disabled={pwSaving || !pw.oldPass || !pw.newPass || !pw.confirm}>
                                {pwSaving ? 'Updating…' : 'Update password'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default Profile;
